import type { LifeCycles } from 'single-spa'
import type { ReducersMapObject } from 'redux'
import { remoteRepository, localRepository } from './constants'
import { getMicroFrontendMetadata, mergeMicroFrontendMetadata, type MicroFrontendManifest } from './manifest'
import logger from '../log'
import { getAttachReducers } from '../ddWindow'

type MicroFrontendApplication = (domElementId: string) => LifeCycles

type ScriptWithReducer = {
  reducer?: ReducersMapObject
}

export type ScriptWithCssFile = {
  hasCssFile?: boolean
}

type ScriptValue = MicroFrontendApplication | ReducersMapObject | boolean | undefined

export type Script = Record<string, ScriptValue> & ScriptWithReducer & ScriptWithCssFile

const injectStylesheet = (href: string) => {
  const link = document.createElement('link')
  link.href = href
  link.type = 'text/css'
  link.rel = 'stylesheet'
  document.getElementsByTagName('head')[0].appendChild(link)
}

export type MicroFrontendScript = {
  name?: string
  script?: Script
  error?: string
  loaderDescription?: string
  scriptUrl?: string
}

type DefaultEsmScript = {
  default: Script
}

type EsmScript = Script | DefaultEsmScript

export const hasCssFile = (script: ScriptWithCssFile): boolean => !!script.hasCssFile

export const resolveMicroFrontendManifest = async (
  manifest: MicroFrontendManifest,
  baseUrl: string,
  local: boolean
): Promise<MicroFrontendManifest> => {
  if (!local) {
    return manifest
  }

  const metadata = await getMicroFrontendMetadata(baseUrl)
  return mergeMicroFrontendMetadata(manifest, metadata)
}

const hasDefaultScript = (script: EsmScript): script is DefaultEsmScript =>
  'default' in script && typeof script.default !== 'function'

export const normalizeEsmScript = (script: EsmScript): Script => (hasDefaultScript(script) ? script.default : script)

const loadScript = async (url: string): Promise<Script> => normalizeEsmScript(await import(/* @vite-ignore */ url))

export const getMicroFrontendLoadErrorMessage = (
  microFront: string,
  scriptUrl: string,
  loaderDescription: string
): string => `Failed to load micro frontend ${microFront} from ${scriptUrl} using ${loaderDescription}`

export const load = async (
  microFront: string,
  manifest: MicroFrontendManifest,
  local = false
): Promise<MicroFrontendScript> => {
  const localBaseUrl = `${localRepository}/${microFront}`
  const baseUrl = local ? `${localBaseUrl}/lib` : `${remoteRepository}/${microFront}/${manifest.version}`
  const metadataBaseUrl = local ? localBaseUrl : baseUrl
  const styleUrl = `${baseUrl}/index.css`
  const scriptUrl = `${baseUrl}/index.js`
  const loadStartedAt = performance.now()
  let loaderDescription = `${manifest.runtime}/${manifest.format}`
  try {
    const resolvedManifest = await resolveMicroFrontendManifest(manifest, metadataBaseUrl, local)
    loaderDescription = `${resolvedManifest.runtime}/${resolvedManifest.format}`
    const loadedScript = await loadScript(scriptUrl)
    if (hasCssFile(loadedScript) || resolvedManifest.hasCss) {
      injectStylesheet(styleUrl)
    }
    const { reducer } = loadedScript
    const attachReducers = getAttachReducers()
    if (reducer && attachReducers) {
      attachReducers(reducer)
    }
    return { name: microFront, script: loadedScript, loaderDescription, scriptUrl }
  } catch (error) {
    logger.error(getMicroFrontendLoadErrorMessage(microFront, scriptUrl, loaderDescription), error, {
      loadDurationMs: Math.round(performance.now() - loadStartedAt)
    })
    return Promise.resolve({ error: 'Failed to load micro frontend script' })
  }
}

export default load
